import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import { QuestionModal, Spinner } from '../../../../../common/index.jsx';
import PropTypes from 'prop-types';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import {
  DislikeOutlined,
  FileDoneOutlined,
  FileExclamationOutlined,
  LikeOutlined,
  FileSearchOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import {
  useAcceptInterestMutation,
  useRejectInterestMutation,
  useFinishUnsuccessfullyMutation,
  useFinishSuccessfullyMutation,
  useDeleteInterestMutation,
} from '../../../../../../redux/services/fij_applications.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PopupWindow from '../../../../../common/modals/PopupWindow/PopupWindow.jsx';
import { Form, Input, InputNumber, Radio, Space, Tag } from 'antd';
import {
  FIJ_INTEREST_STATUS,
  FIJ_INTEREST_REJECT_REASONS,
  statusColorInterest,
} from '../../../../../../utils/constants/enums.js';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import FormItem from 'antd/es/form/FormItem';
import { required } from '../../../../../antd/form/FormValidation.js';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';
import { useStateFriPraxInterestsDataGridFilter } from '../../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';

const FijInterestsTable = ({
  interests,
  internalJob,
  isFijAdmin,
  refetchData,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const [rejectInterestId, setRejectInterestId] = useState(undefined);
  const [hoursInterestId, setHoursInterestId] = useState(undefined);
  const [failedInterestId, setFailedInterestId] = useState(undefined);
  const [acceptInterestId, setAcceptInterestId] = useState(undefined);
  const [deleteInterestId, setDeleteInterestId] = useState(undefined);

  const [rejectReasonOptionValue, setRejectReasonOptionValue] = useState(1);
  const onRejectReasonOptionChange = (e) => {
    setRejectReasonOptionValue(e.target.value);
  };
  const { saveSuccess, deleteSuccess } = useToastMessages();
  const [acceptInterest, { isSuccess: acceptedInterest }] =
    useAcceptInterestMutation();
  const [rejectInterest, { isSuccess: rejectedInterest }] =
    useRejectInterestMutation();
  const [finishUnsuccessfully, { isSuccess: finishedUnsuccessfully }] =
    useFinishUnsuccessfullyMutation();
  const [finishSuccessfully, { isSuccess: finishedSuccessfully }] =
    useFinishSuccessfullyMutation();
  const [deleteInterest, { isSuccess: deletedInterest }] =
    useDeleteInterestMutation();

  const successWithProjectSubmit = (values) => {
    finishSuccessfully({
      interestId: hoursInterestId,
      hours_worked: values.hoursSpend,
    });
    setHoursInterestId(undefined);
  };

  const archived = internalJob?.archived_at !== null;
  const isSupervisor =
    !!user?.id && Number(internalJob?.supervisor_id) === Number(user.id);
  const canDeleteInterest = !archived && (isFijAdmin || isSupervisor);
  const rejectReasons = FIJ_INTEREST_REJECT_REASONS;

  const finishFailedConfirm = () => {
    finishUnsuccessfully(failedInterestId);
    setFailedInterestId(undefined);
  };

  const rejectInterestSubmit = (values) => {
    rejectInterest({
      interestId: rejectInterestId,
      reason:
        rejectReasonOptionValue !== 4
          ? rejectReasons[rejectReasonOptionValue - 1].label
          : values.reason,
    });
    setRejectInterestId(undefined);
  };

  const acceptInterestConfirm = () => {
    if (!acceptInterestId) {
      return;
    }

    acceptInterest(acceptInterestId);
    setAcceptInterestId(undefined);
  };

  const deleteInterestConfirm = () => {
    if (!deleteInterestId) {
      return;
    }

    deleteInterest(deleteInterestId);
    setDeleteInterestId(undefined);
  };

  useEffect(() => {
    if (deletedInterest) {
      deleteSuccess();
      refetchData();
    }
  }, [deletedInterest]);

  useEffect(() => {
    if (
      acceptedInterest ||
      rejectedInterest ||
      finishedUnsuccessfully ||
      finishedSuccessfully
    ) {
      saveSuccess();
      refetchData();
    }
  }, [
    acceptedInterest,
    rejectedInterest,
    finishedUnsuccessfully,
    finishedSuccessfully,
  ]);

  const customSettings = (interest) => {
    return (
      <div style={{ display: 'flex' }}>
        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Zobraziť náhľad prihlášky')}
          onClick={() => onDetailClick(interest.id)}
        />

        {interest.status === FIJ_INTEREST_STATUS.WAITING && !archived && (
          <IconButton
            icon={<LikeOutlined />}
            tooltip={t('Prijať na FRI prax')}
            onClick={() => {
              setAcceptInterestId(interest.id);
            }}
          />
        )}

        {interest.status === FIJ_INTEREST_STATUS.WAITING && !archived && (
          <IconButton
            icon={<DislikeOutlined />}
            tooltip={t('Neprijať na FRI prax')}
            onClick={() => {
              setRejectInterestId(interest.id);
            }}
          />
        )}

        {isFijAdmin &&
          interest.status === FIJ_INTEREST_STATUS.ACCEPTED &&
          !archived && (
            <IconButton
              icon={<FileDoneOutlined />}
              tooltip={t('Úspešne ukončiť FRI prax')}
              onClick={() => {
                setHoursInterestId(interest.id);
              }}
            />
          )}

        {isFijAdmin &&
          interest.status === FIJ_INTEREST_STATUS.ACCEPTED &&
          !archived && (
            <IconButton
              icon={<FileExclamationOutlined />}
              tooltip={t('Neúspešne ukončiť FRI prax')}
              onClick={() => {
                setFailedInterestId(interest.id);
              }}
            />
          )}

        {canDeleteInterest && (
          <IconButton
            icon={<UserDeleteOutlined />}
            tooltip={t('Odstrániť študenta z FRI praxe')}
            onClick={() => {
              setDeleteInterestId(interest.id);
            }}
          />
        )}
      </div>
    );
  };

  const onDetailClick = (id) => {
    navigate(`${routes.cmsFijInterestDetail}/${id}`);
  };

  const initialValuesDeclineOfInterest = {
    reason: '',
  };

  const customSettingsForPopupWindow = () => {
    return (
      <>
        <div style={{ padding: '10px' }}>
          <Form
            onFinish={rejectInterestSubmit}
            initialValues={initialValuesDeclineOfInterest}
          >
            <FormItem rules={[required(t)]}>
              <Radio.Group
                onChange={onRejectReasonOptionChange}
                value={rejectReasonOptionValue}
              >
                <Space orientation="vertical">
                  <Radio style={{ fontWeight: 'normal' }} value={1}>
                    {t(rejectReasons[0].label)}
                  </Radio>
                  <Radio style={{ fontWeight: 'normal' }} value={2}>
                    {t(rejectReasons[1].label)}
                  </Radio>
                  <Radio style={{ fontWeight: 'normal' }} value={3}>
                    {t(rejectReasons[2].label)}
                  </Radio>
                  <Radio style={{ fontWeight: 'normal' }} value={4}>
                    {t('iný dôvod')}
                  </Radio>
                </Space>
              </Radio.Group>
            </FormItem>
            {rejectReasonOptionValue === 4 ? (
              <FormItem name="reason" rules={[required(t)]}>
                <Input.TextArea
                  autoSize={{
                    minRows: 4,
                  }}
                  placeholder={t('Zadajte dôvod')}
                  style={{
                    width: '100%',
                  }}
                />
              </FormItem>
            ) : null}
            <FormButtonsContainer>
              <FormButton
                onClick={() => {
                  setRejectInterestId(undefined);
                }}
              >
                {t('Zrušiť')}
              </FormButton>
              <FormButton submitOnClick={true} submitKey={'interest'}>
                {t('Odoslať')}
              </FormButton>
            </FormButtonsContainer>
          </Form>
        </div>
      </>
    );
  };

  const initialValueForHours = {
    hoursSpend: 0,
  };

  const customSettingsForShowHoursSpend = () => {
    return (
      <>
        <div style={{ padding: '10px' }}>
          <Form
            onFinish={successWithProjectSubmit}
            initialValues={initialValueForHours}
          >
            <FormItem
              label={t('Počet hodín')}
              name="hoursSpend"
              rules={[
                required(t),
                {
                  type: 'number',
                  min: 1,
                },
              ]}
            >
              <InputNumber style={{ width: 300 }} />
            </FormItem>

            <FormButtonsContainer>
              <FormButton
                onClick={() => {
                  setHoursInterestId(undefined);
                }}
              >
                {t('Zrušiť')}
              </FormButton>
              <FormButton submitOnClick={true} submitKey={'interest'}>
                {t('Odoslať')}
              </FormButton>
            </FormButtonsContainer>
          </Form>
        </div>
      </>
    );
  };

  const studentNameTextFilterProps = useDataGridTextFilter('student_full_name');
  const schoolGroupTextFilterProps = useDataGridTextFilter('school_group');
  const dateFilterProps = useDataGridTextFilter('date_enrollment');
  const statusFilterProps = useStateFriPraxInterestsDataGridFilter('status');

  const dataGridColumns = useMemo(() => [
    {
      title: t('Študent'),
      dataIndex: 'student_full_name',
      ellipsis: true,
      ...studentNameTextFilterProps,
      sorter: dataGridTextSorter('student_full_name'),
    },
    {
      title: t('Študijná skupina'),
      dataIndex: 'school_group',
      ellipsis: true,
      ...schoolGroupTextFilterProps,
      sorter: dataGridTextSorter('school_group'),
    },
    {
      title: t('Dátum prihlásenia'),
      dataIndex: 'date_enrollment',
      ellipsis: true,
      sorter: dataGridDateSorter('date_enrollment'),
      ...dateFilterProps,
      render: (date) => (date && new Date(date).toLocaleDateString()) || '-',
    },
    {
      title: t('Stav'),
      dataIndex: 'status',
      ellipsis: true,
      sorter: dataGridTextSorter('status'),
      ...statusFilterProps,
      render: (status) => (
        <Tag color={statusColorInterest(status)}>{t(status)}</Tag>
      ),
    },
  ]);

  return !interests ? (
    <Spinner />
  ) : (
    <>
      <DataGrid
        rowKey={(interest) => interest.id}
        columns={dataGridColumns}
        dataSource={interests || []}
        permissionsBasic={'fri-internal-jobs-interests'}
        uniqueTableId={'fri-internal-jobs-interests-table'}
        customSettings={customSettings}
        optionsWidth={225}
        onRowDetailClick={(interest) => onDetailClick(interest.id)}
      />

      <PopupWindow
        headerTitle={t('Neprijať študenta na FRI prax')}
        visible={(rejectInterestId && true) || false}
        toggle={() => setRejectInterestId(undefined)}
        question={t(
          'Vyberte dôvod, prečo nechcete prijať študenta na FRI prax',
        )}
        customSettings={customSettingsForPopupWindow()}
      />

      <PopupWindow
        headerTitle={t('Nastaviť úspešné ukončenie FRI praxe')}
        visible={(hoursInterestId && true) || false}
        toggle={() => setHoursInterestId(undefined)}
        question={t(
          'Nastavte počet hodín, ktoré študent odpracoval počas FRI praxe',
        )}
        customSettings={customSettingsForShowHoursSpend()}
      />

      <QuestionModal
        headerTitle={t('Nastaviť neúspešné ukončenie FRI praxe')}
        visible={(failedInterestId && true) || false}
        toggle={() => setFailedInterestId(undefined)}
        question={t(
          'Naozaj chcete študentovi nastaviť, že ukončil FRI prax neúspešne?',
        )}
        yes={t('Áno')}
        chosenPositive={finishFailedConfirm}
      />

      <QuestionModal
        headerTitle={t('Prijať študenta na FRI prax')}
        visible={(acceptInterestId && true) || false}
        toggle={() => setAcceptInterestId(undefined)}
        question={t(
          'Naozaj chcete prijať študenta na FRI prax? Operáciu nie je možné vrátiť späť!',
        )}
        yes={t('Áno')}
        chosenPositive={acceptInterestConfirm}
      />

      <QuestionModal
        headerTitle={t('Odstrániť študenta z FRI praxe')}
        visible={(deleteInterestId && true) || false}
        toggle={() => setDeleteInterestId(undefined)}
        question={t(
          'Naozaj chcete odstrániť študenta z FRI praxe? Operáciu nie je možné vrátiť späť!',
        )}
        yes={t('Áno')}
        chosenPositive={deleteInterestConfirm}
      />
    </>
  );
};

FijInterestsTable.prototype = {
  interests: PropTypes.any,
  archived: PropTypes.bool,
  isManager: PropTypes.bool,
  onCreateClick: PropTypes.func,
  onDelete: PropTypes.func,
  fijIsFinished: PropTypes.bool,
  fijInProgress: PropTypes.bool,
};

export default FijInterestsTable;
